/**
 * Pricing Plans page — public read view + admin edit.
 *  - Tabs: Clubes | Padelistas
 *  - Cards with price range, setup fee, features, highlight variant.
 *  - Admins can edit plans and configure alternative payment methods per plan.
 *  - Subscribing opens a dialog with Stripe + collapsible "Otros métodos de pago".
 *    Alt methods: link, bank transfer, PayPal, manual instructions. Optional receipt upload.
 */
import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Buildings, UsersThree, Trophy, Broadcast, ThumbsUp, PencilSimple, Sparkle, Check, Info,
  CreditCard, Bank, Link as LinkIcon, Hand, CaretDown, CaretUp, Plus, Trash, Upload,
} from "@phosphor-icons/react";

const ICONS = { buildings: Buildings, broadcast: Broadcast, trophy: Trophy, thumbsup: ThumbsUp };

function formatPrice(p) {
  if (p === 0 || p === "0") return "Gratis";
  if (p === null || p === undefined || p === "") return null;
  return `$${Number(p).toLocaleString("es-MX")}`;
}

const METHOD_TYPES = [
  { value: "link", label: "Link externo", icon: LinkIcon },
  { value: "bank", label: "Transferencia bancaria", icon: Bank },
  { value: "paypal", label: "PayPal", icon: LinkIcon },
  { value: "manual", label: "Instrucciones manuales", icon: Hand },
];

function PlanCard({ plan, isAdmin, onEdit, onSubscribe, onMethods, onDelete }) {
  const Icon = ICONS[plan.icon] || Trophy;
  const min = formatPrice(plan.monthly_price_min);
  const max = formatPrice(plan.monthly_price_max);
  const range = min && max && plan.monthly_price_min !== plan.monthly_price_max
    ? `${min} – ${max}`
    : min || "Personalizado";
  const isFree = plan.monthly_price_min === 0 && plan.monthly_price_max === 0;
  const setup = plan.setup_price > 0
    ? plan.setup_price_max
      ? `Setup: $${Number(plan.setup_price).toLocaleString("es-MX")} – $${Number(plan.setup_price_max).toLocaleString("es-MX")} ${plan.currency}`
      : `Setup: $${Number(plan.setup_price).toLocaleString("es-MX")} ${plan.currency}`
    : null;
  const methodsCount = (plan.payment_methods || []).filter(m => m.enabled).length;

  return (
    <div
      className={`card-tactical relative p-6 flex flex-col ${plan.highlight ? "border-2" : ""}`}
      style={{ borderColor: plan.highlight ? plan.color : undefined }}
      data-testid={`plan-card-${plan.id}`}
    >
      {plan.highlight && (
        <Badge
          className="absolute -top-3 left-6 font-bold uppercase tracking-widest text-[10px] px-2 py-1"
          style={{ backgroundColor: plan.color, color: "#000" }}
        >
          <Sparkle size={10} weight="fill" className="mr-1" /> Destacado
        </Badge>
      )}
      <div className="flex items-start justify-between gap-2 mb-4">
        <div
          className="w-12 h-12 flex items-center justify-center border"
          style={{ borderColor: plan.color, color: plan.color, backgroundColor: `${plan.color}10` }}
        >
          <Icon size={24} weight="duotone" />
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-1 justify-end">
            <Button size="sm" variant="outline" onClick={() => onMethods(plan)} className="border-white/10 text-gray-400 hover:text-white text-[10px] uppercase h-7" data-testid={`plan-methods-${plan.id}`}>
              <CreditCard size={12} className="mr-1" /> Métodos {methodsCount > 0 && `(${methodsCount})`}
            </Button>
            <Button size="sm" variant="outline" onClick={() => onEdit(plan)} className="border-white/10 text-gray-400 hover:text-white text-[10px] uppercase h-7" data-testid={`plan-edit-${plan.id}`}>
              <PencilSimple size={12} className="mr-1" /> Editar
            </Button>
            <Button size="sm" variant="outline" onClick={() => onDelete(plan)} className="border-red-500/30 text-red-400 hover:text-red-300 hover:bg-red-500/10 text-[10px] uppercase h-7" data-testid={`plan-delete-${plan.id}`}>
              <Trash size={12} />
            </Button>
          </div>
        )}
      </div>
      <h3 className="font-['Barlow_Condensed'] text-2xl sm:text-3xl font-black uppercase leading-tight" style={{ color: plan.color }}>
        {plan.name}
      </h3>
      <p className="text-xs text-gray-400 mt-1 mb-4">{plan.tagline}</p>

      <div className="border-y border-white/10 py-4 my-2">
        <p className="font-['Barlow_Condensed'] font-black text-3xl sm:text-4xl leading-none" style={{ color: plan.color }} data-testid={`plan-price-${plan.id}`}>
          {isFree ? "Gratis" : range}
        </p>
        {!isFree && (
          <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mt-1">{plan.currency} / mes</p>
        )}
        {setup && (
          <p className="text-[10px] uppercase tracking-widest text-amber-400 mt-2 font-['JetBrains_Mono']">{setup}</p>
        )}
      </div>

      <ul className="space-y-2 mb-4 flex-1" data-testid={`plan-features-${plan.id}`}>
        {(plan.features || []).map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
            <Check size={14} weight="bold" className="shrink-0 mt-1" style={{ color: plan.color }} />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {!isFree && plan.monthly_price_min > 0 ? (
        <Button
          onClick={() => onSubscribe(plan)}
          className="w-full font-bold uppercase tracking-wide text-xs h-10"
          style={{ backgroundColor: plan.color, color: "#000" }}
          data-testid={`plan-subscribe-${plan.id}`}
        >
          Suscribirme
        </Button>
      ) : (
        <Badge variant="outline" className="bg-white/5 text-gray-400 border-white/10 justify-center py-2 text-[10px] uppercase tracking-widest" data-testid={`plan-cta-free-${plan.id}`}>
          ✓ Activo automáticamente
        </Badge>
      )}
    </div>
  );
}

const EMPTY_FORM = {
  id: "", type: "user", name: "", tagline: "", monthly_price_min: 0, monthly_price_max: 0,
  setup_price: 0, setup_price_max: 0, currency: "MXN", color: "#CCFF00",
  features: "", highlight: false, sort_order: 99,
};

function newMethod(type) {
  return {
    id: `m_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    type, label: METHOD_TYPES.find(t => t.value === type)?.label || type,
    enabled: true, instructions: "",
    ...(type === "link" ? { url: "" }
      : type === "bank" ? { bank_name: "", clabe: "", account_holder: "", account_number: "", reference: "" }
      : type === "paypal" ? { paypal_email: "", url: "" }
      : {}),
  };
}

/* ---------- Payment methods editor (used by both plan-admin and club-admin) ---------- */
export function PaymentMethodsEditor({ value, onChange }) {
  const items = value || [];
  const update = (idx, patch) => onChange(items.map((m, i) => i === idx ? { ...m, ...patch } : m));
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = (type) => onChange([...items, newMethod(type)]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {METHOD_TYPES.map(t => (
          <Button key={t.value} size="sm" variant="outline" onClick={() => add(t.value)} className="border-white/10 text-xs uppercase" data-testid={`add-method-${t.value}`}>
            <Plus size={12} className="mr-1" /> {t.label}
          </Button>
        ))}
      </div>
      {items.length === 0 && (
        <p className="text-xs text-gray-500 italic">Aún no hay métodos alternativos. Agrega al menos uno.</p>
      )}
      {items.map((m, idx) => {
        const TypeIcon = METHOD_TYPES.find(t => t.value === m.type)?.icon || LinkIcon;
        return (
          <div key={m.id} className="border border-white/10 bg-[#0A0A0A] p-4 space-y-3" data-testid={`method-row-${idx}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <TypeIcon size={18} weight="duotone" className="text-[#CCFF00]" />
                <Badge variant="outline" className="text-[10px] uppercase border-white/10 text-gray-300">{m.type}</Badge>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer text-xs uppercase tracking-widest text-gray-300">
                  <input type="checkbox" checked={!!m.enabled} onChange={(e) => update(idx, { enabled: e.target.checked })} className="w-4 h-4 accent-[#CCFF00]" />
                  Activo
                </label>
                <Button size="sm" variant="ghost" onClick={() => remove(idx)} className="text-red-400 hover:text-red-300 h-7 px-2" data-testid={`remove-method-${idx}`}>
                  <Trash size={14} />
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">Etiqueta visible</Label>
              <Input value={m.label || ""} onChange={(e) => update(idx, { label: e.target.value })} className="bg-[#141414] border-white/20 h-9" placeholder="Ej. Transferencia BBVA" />
            </div>
            {m.type === "link" && (
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-400">URL del link de pago</Label>
                <Input value={m.url || ""} onChange={(e) => update(idx, { url: e.target.value })} className="bg-[#141414] border-white/20 h-9" placeholder="https://mpago.la/..., https://buy.stripe.com/...," />
              </div>
            )}
            {m.type === "paypal" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-400">Email / @ PayPal</Label>
                  <Input value={m.paypal_email || ""} onChange={(e) => update(idx, { paypal_email: e.target.value })} className="bg-[#141414] border-white/20 h-9" placeholder="usuario@correo.com" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-400">URL PayPal.me (opcional)</Label>
                  <Input value={m.url || ""} onChange={(e) => update(idx, { url: e.target.value })} className="bg-[#141414] border-white/20 h-9" placeholder="https://paypal.me/usuario" />
                </div>
              </div>
            )}
            {m.type === "bank" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-400">Banco</Label>
                  <Input value={m.bank_name || ""} onChange={(e) => update(idx, { bank_name: e.target.value })} className="bg-[#141414] border-white/20 h-9" placeholder="BBVA" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-400">Titular</Label>
                  <Input value={m.account_holder || ""} onChange={(e) => update(idx, { account_holder: e.target.value })} className="bg-[#141414] border-white/20 h-9" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-400">CLABE</Label>
                  <Input value={m.clabe || ""} onChange={(e) => update(idx, { clabe: e.target.value })} className="bg-[#141414] border-white/20 h-9 font-['JetBrains_Mono']" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-400">Cuenta / Tarjeta</Label>
                  <Input value={m.account_number || ""} onChange={(e) => update(idx, { account_number: e.target.value })} className="bg-[#141414] border-white/20 h-9 font-['JetBrains_Mono']" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-gray-400">Referencia / concepto</Label>
                  <Input value={m.reference || ""} onChange={(e) => update(idx, { reference: e.target.value })} className="bg-[#141414] border-white/20 h-9" placeholder="Indica al usuario qué poner en el concepto" />
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">Instrucciones adicionales</Label>
              <Textarea value={m.instructions || ""} onChange={(e) => update(idx, { instructions: e.target.value })} rows={2} className="bg-[#141414] border-white/20" placeholder="Cómo confirmar el pago, en qué horario, etc." />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- Subscribe dialog (Stripe + alternative methods) ---------- */
const DURATION_OPTIONS = [
  { months: 1, discount: 0, label: "1 mes" },
  { months: 3, discount: 5, label: "3 meses" },
  { months: 6, discount: 10, label: "6 meses" },
  { months: 12, discount: 15, label: "1 año" },
];

function SubscribeDialog({ plan, open, onClose, userClubs }) {
  const [opts, setOpts] = useState(null);
  const [clubId, setClubId] = useState("");
  const [months, setMonths] = useState(1);
  const [showAlt, setShowAlt] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!plan || !open) return;
    const q = clubId ? `?club_id=${clubId}` : "";
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/pricing/plans/${plan.id}/payment-options${q}`);
        if (!cancelled) setOpts(data);
      } catch {
        if (!cancelled) toast.error("No se pudieron cargar las opciones");
      }
    })();
    return () => { cancelled = true; };
  }, [plan, open, clubId]);

  if (!plan) return null;

  const monthly = Number(plan.monthly_price_min || 0);
  const setup = Number(plan.setup_price || 0);
  const dur = DURATION_OPTIONS.find(d => d.months === months) || DURATION_OPTIONS[0];
  const subtotal = monthly * months;
  const discountAmount = Math.round((subtotal * dur.discount) / 100 * 100) / 100;
  const total = Math.round((subtotal - discountAmount + setup) * 100) / 100;

  const payWithStripe = async () => {
    try {
      const origin_url = window.location.origin;
      const { data } = await api.post(`/pricing/plans/${plan.id}/checkout`, { months, origin_url });
      if (data?.url) window.location.href = data.url;
      else toast.error("No se pudo iniciar el checkout");
    } catch (err) { toast.error(err.response?.data?.detail || "Error al iniciar pago"); }
  };

  const submitManual = async () => {
    if (!selectedMethod) return;
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("plan_id", plan.id);
      form.append("method_id", selectedMethod.id);
      form.append("months", String(months));
      if (clubId) form.append("club_id", clubId);
      if (notes) form.append("notes", notes);
      if (file) form.append("file", file);
      await api.post("/manual-payments/submit", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Pago enviado. Un administrador lo revisará pronto.");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar el pago");
    } finally { setSubmitting(false); }
  };

  const allAlt = opts ? [...(opts.plan_payment_methods || []), ...(opts.club_payment_methods || [])] : [];
  const isUserPlan = plan.type === "user";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#141414] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="subscribe-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] text-3xl uppercase">Suscribirme a {plan.name}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-400">
          Monto mensual: <span className="text-[#CCFF00] font-semibold">${Number(plan.monthly_price_min).toLocaleString("es-MX")} {plan.currency}</span>
          {plan.setup_price > 0 && <> · Setup inicial ${Number(plan.setup_price).toLocaleString("es-MX")} {plan.currency}</>}
        </p>

        {isUserPlan && Array.isArray(userClubs) && userClubs.length > 0 && (
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-400">Club (opcional, para pagar con métodos del club)</Label>
            <select value={clubId} onChange={(e) => setClubId(e.target.value)} className="w-full bg-[#0A0A0A] border border-white/20 h-10 px-3 text-sm" data-testid="club-selector">
              <option value="">— Ninguno (pago directo a la plataforma) —</option>
              {userClubs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}

        {/* Duration selector with progressive discount */}
        <div data-testid="duration-selector">
          <Label className="text-xs uppercase tracking-widest text-gray-400 mb-2 block">Duración (descuento progresivo)</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {DURATION_OPTIONS.map(d => {
              const active = months === d.months;
              return (
                <button
                  key={d.months}
                  type="button"
                  onClick={() => setMonths(d.months)}
                  className={`relative border p-3 text-center transition-all ${active ? "border-[#CCFF00] bg-[#CCFF00]/10" : "border-white/10 bg-[#0A0A0A] hover:border-white/30"}`}
                  data-testid={`duration-${d.months}`}
                >
                  <p className={`font-['Barlow_Condensed'] text-lg font-bold uppercase ${active ? "text-[#CCFF00]" : "text-white"}`}>{d.label}</p>
                  {d.discount > 0 ? (
                    <p className="text-[10px] uppercase tracking-widest text-green-400 mt-1">-{d.discount}% off</p>
                  ) : (
                    <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">sin descuento</p>
                  )}
                </button>
              );
            })}
          </div>
          <div className="mt-3 bg-[#0A0A0A] border border-white/10 p-3 text-xs space-y-1 font-['JetBrains_Mono']" data-testid="duration-summary">
            <div className="flex justify-between text-gray-400">
              <span>Subtotal ({months} × ${monthly.toLocaleString("es-MX")} {plan.currency})</span>
              <span className="text-white">${subtotal.toLocaleString("es-MX")}</span>
            </div>
            {dur.discount > 0 && (
              <div className="flex justify-between text-green-400">
                <span>Descuento {dur.discount}%</span>
                <span>- ${discountAmount.toLocaleString("es-MX")}</span>
              </div>
            )}
            {setup > 0 && (
              <div className="flex justify-between text-gray-400">
                <span>Setup inicial</span>
                <span className="text-white">+ ${setup.toLocaleString("es-MX")}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-white/10 pt-1 mt-1">
              <span className="text-white font-bold uppercase">Total</span>
              <span className="text-[#CCFF00] font-bold text-sm" data-testid="duration-total">${total.toLocaleString("es-MX")} {plan.currency}</span>
            </div>
          </div>
        </div>

        {/* Stripe primary */}
        <div className="border border-white/10 bg-[#0A0A0A] p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <CreditCard size={24} weight="duotone" className="text-[#CCFF00]" />
              <div>
                <p className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-white">Pago con tarjeta (Stripe)</p>
                <p className="text-xs text-gray-400">Activación inmediata, recurrente.</p>
              </div>
            </div>
            <Button
              onClick={payWithStripe}
              disabled={!opts?.stripe_enabled}
              className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-xs"
              data-testid="pay-stripe-btn"
            >
              {opts?.stripe_enabled ? "Pagar con Stripe" : "Stripe no configurado"}
            </Button>
          </div>
        </div>

        {/* Alternative methods — collapsible */}
        <button
          type="button"
          onClick={() => setShowAlt(s => !s)}
          className="w-full flex items-center justify-between bg-[#0A0A0A] border border-white/10 px-4 py-3 hover:bg-white/5"
          data-testid="toggle-alt-methods"
        >
          <span className="text-sm uppercase tracking-widest text-gray-300 font-semibold">Otros métodos de pago {allAlt.length > 0 && `(${allAlt.length})`}</span>
          {showAlt ? <CaretUp size={16} /> : <CaretDown size={16} />}
        </button>

        {showAlt && (
          <div className="space-y-3" data-testid="alt-methods-section">
            {allAlt.length === 0 && (
              <p className="text-xs text-gray-500 italic px-2">No hay métodos alternativos configurados para este plan{clubId ? " ni para este club" : ""}.</p>
            )}
            {allAlt.map((m) => {
              const TypeIcon = METHOD_TYPES.find(t => t.value === m.type)?.icon || LinkIcon;
              const isSelected = selectedMethod?.id === m.id;
              return (
                <div key={m.id} className={`border p-4 ${isSelected ? "border-[#CCFF00] bg-[#CCFF00]/5" : "border-white/10 bg-[#0A0A0A]"}`} data-testid={`alt-method-${m.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2">
                      <TypeIcon size={20} weight="duotone" className="text-[#CCFF00]" />
                      <p className="font-['Barlow_Condensed'] text-lg font-bold uppercase text-white">{m.label}</p>
                    </div>
                    <Button size="sm" variant={isSelected ? "default" : "outline"} onClick={() => setSelectedMethod(isSelected ? null : m)} className={isSelected ? "bg-[#CCFF00] text-black text-xs uppercase" : "border-white/10 text-xs uppercase"} data-testid={`select-method-${m.id}`}>
                      {isSelected ? "Seleccionado" : "Usar este"}
                    </Button>
                  </div>
                  {m.type === "link" && m.url && (
                    <a href={m.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#CCFF00] hover:underline break-all" data-testid={`method-link-${m.id}`}>{m.url}</a>
                  )}
                  {m.type === "paypal" && (
                    <div className="text-xs text-gray-300 space-y-1">
                      {m.paypal_email && <p>PayPal: <span className="text-white font-['JetBrains_Mono']">{m.paypal_email}</span></p>}
                      {m.url && <a href={m.url} target="_blank" rel="noopener noreferrer" className="text-[#CCFF00] hover:underline">{m.url}</a>}
                    </div>
                  )}
                  {m.type === "bank" && (
                    <div className="text-xs text-gray-300 grid grid-cols-1 md:grid-cols-2 gap-2 font-['JetBrains_Mono']">
                      {m.bank_name && <p>Banco: <span className="text-white">{m.bank_name}</span></p>}
                      {m.account_holder && <p>Titular: <span className="text-white">{m.account_holder}</span></p>}
                      {m.clabe && <p>CLABE: <span className="text-white">{m.clabe}</span></p>}
                      {m.account_number && <p>Cuenta: <span className="text-white">{m.account_number}</span></p>}
                      {m.reference && <p className="md:col-span-2">Concepto: <span className="text-white">{m.reference}</span></p>}
                    </div>
                  )}
                  {m.instructions && <p className="text-xs text-gray-400 mt-2 whitespace-pre-line">{m.instructions}</p>}
                </div>
              );
            })}

            {selectedMethod && (
              <div className="border border-[#CCFF00]/40 bg-[#CCFF00]/5 p-4 space-y-3" data-testid="manual-submit-section">
                <p className="text-sm text-white font-semibold">Confirma tu pago con {selectedMethod.label}</p>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-400">Comprobante (imagen o PDF, opcional)</Label>
                  <Input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="bg-[#0A0A0A] border-white/20 h-10 file:text-white file:bg-white/10 file:border-0 file:mr-3 file:py-1 file:px-3" data-testid="manual-file" />
                  {file && <p className="text-xs text-gray-400 mt-1">{file.name}</p>}
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-400">Notas (opcional)</Label>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="bg-[#0A0A0A] border-white/20" placeholder="Folio, banco origen, hora, etc." data-testid="manual-notes" />
                </div>
                <Button onClick={submitManual} disabled={submitting} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase w-full" data-testid="manual-submit-btn">
                  <Upload size={14} className="mr-2" />
                  {submitting ? "Enviando..." : (file ? "Enviar comprobante" : "Enviar solicitud para revisión")}
                </Button>
                <p className="text-[11px] text-gray-500">Un administrador revisará tu pago y activará la suscripción manualmente.</p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-white/10">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Page ---------- */
export default function PricingPlans() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("user");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [methodsEditing, setMethodsEditing] = useState(null);
  const [methodsList, setMethodsList] = useState([]);
  const [subscribePlan, setSubscribePlan] = useState(null);
  const [userClubs, setUserClubs] = useState([]);

  const load = () => {
    setLoading(true);
    api.get("/pricing/plans").then(({ data }) => setPlans(data || [])).catch(() => toast.error("Error")).finally(() => setLoading(false));
  };
  const loadClubs = async () => {
    try {
      const { data } = await api.get("/clubs");
      setUserClubs(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); loadClubs(); }, []);

  const filtered = useMemo(() => (plans || []).filter((p) => p.type === tab).slice().sort((a, b) => (Number(b.monthly_price_min) || 0) - (Number(a.monthly_price_min) || 0)), [plans, tab]);

  const seedDefaults = async () => {
    if (!window.confirm("¿Crear los planes por defecto? (5 planes: 3 clubes + 2 padelistas). Es seguro: no duplica.")) return;
    try {
      const { data } = await api.post("/pricing/plans/seed");
      toast.success(`${data.created_count} planes creados`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const openEdit = (plan) => {
    setEditing(plan);
    setForm({
      ...plan,
      features: (plan.features || []).join("\n"),
      monthly_price_max: plan.monthly_price_max ?? "",
      setup_price_max: plan.setup_price_max ?? "",
    });
  };

  const openMethods = (plan) => {
    setMethodsEditing(plan);
    setMethodsList(plan.payment_methods || []);
  };

  const saveMethods = async () => {
    try {
      await api.put(`/pricing/plans/${methodsEditing.id}/payment-methods`, { payment_methods: methodsList });
      toast.success("Métodos guardados");
      setMethodsEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
  };

  const submit = async () => {
    try {
      const payload = { ...form };
      if (payload.monthly_price_max === "") payload.monthly_price_max = null;
      if (payload.setup_price_max === "") payload.setup_price_max = null;
      if (editing?.__create) {
        if (!payload.name || !payload.type) { toast.error("Nombre y tipo son obligatorios"); return; }
        await api.post(`/pricing/plans`, payload);
        toast.success("Plan creado");
      } else {
        await api.put(`/pricing/plans/${editing.id}`, payload);
        toast.success("Plan actualizado");
      }
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, type: tab });
    setEditing({ __create: true, name: "Nuevo plan" });
  };

  const handleDelete = async (plan) => {
    if (!window.confirm(`¿Eliminar el plan "${plan.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/pricing/plans/${plan.id}`);
      toast.success("Plan eliminado");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar"); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="pricing-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Planes y Precios</p>
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">Únete a la red padelista</h1>
          <p className="text-sm text-gray-400 mt-1 max-w-xl">
            Elige el plan que mejor se adapte a tu club o a ti. Pagos en MXN, recurrencia mensual.
            {isAdmin && " · Como administrador puedes editar precios, características y métodos de pago alternativos."}
          </p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <Button onClick={openCreate} variant="outline" className="border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00]/10 font-bold uppercase tracking-wide text-xs" data-testid="create-plan-btn">
              <Plus size={14} className="mr-1" /> Crear plan
            </Button>
            {plans.length === 0 && (
              <Button onClick={seedDefaults} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide text-xs" data-testid="seed-plans-btn">
                <Sparkle size={16} className="mr-2" /> Cargar planes por defecto
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="inline-flex bg-[#0A0A0A] border border-white/10 p-1" data-testid="pricing-tabs">
        <button onClick={() => setTab("user")} className={`px-5 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${tab === "user" ? "bg-[#CCFF00] text-black" : "text-gray-400 hover:text-white"}`} data-testid="tab-user">
          <UsersThree size={14} className="inline mr-2 -mt-0.5" /> Padelistas
        </button>
        <button onClick={() => setTab("club")} className={`px-5 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${tab === "club" ? "bg-[#CCFF00] text-black" : "text-gray-400 hover:text-white"}`} data-testid="tab-club">
          <Buildings size={14} className="inline mr-2 -mt-0.5" /> Clubes
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="card-tactical p-10 text-center">
          <Info size={32} className="text-gray-600 mx-auto mb-3" />
          <p className="text-sm text-gray-400">{isAdmin ? "Sin planes aún. Pulsa \"Cargar planes por defecto\" para crear los iniciales." : "Planes próximamente."}</p>
        </div>
      ) : (
        <div className={`grid gap-5 ${tab === "club" ? "md:grid-cols-2 lg:grid-cols-3" : "md:grid-cols-2"}`} data-testid="plans-grid">
          {filtered.map((p) => <PlanCard key={p.id} plan={p} isAdmin={isAdmin} onEdit={openEdit} onMethods={openMethods} onSubscribe={setSubscribePlan} onDelete={handleDelete} />)}
        </div>
      )}

      {tab === "club" && filtered.some((p) => p.setup_price > 0) && (
        <div className="card-tactical p-5 border-amber-500/30" data-testid="setup-info">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-amber-400 mb-2 flex items-center gap-2"><Info size={14} /> Información de pago inicial</p>
          <p className="text-sm text-gray-300 leading-relaxed">
            Pago inicial por club de <span className="text-amber-400 font-semibold">$5,000 MXN (2 streamings)</span> a <span className="text-amber-400 font-semibold">$10,000 MXN (8 streamings)</span> por la configuración de cámaras y capacitación. Después se mantiene la mensualidad recurrente del plan elegido.
          </p>
        </div>
      )}

      {/* Subscribe dialog */}
      <SubscribeDialog key={subscribePlan?.id || "none"} plan={subscribePlan} open={!!subscribePlan} onClose={() => setSubscribePlan(null)} userClubs={userClubs} />

      {/* Methods admin dialog */}
      <Dialog open={!!methodsEditing} onOpenChange={(v) => !v && setMethodsEditing(null)}>
        <DialogContent className="bg-[#141414] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="methods-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">Métodos de pago — {methodsEditing?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-400">Configura los métodos alternativos a Stripe que los usuarios verán al suscribirse a este plan.</p>
          <PaymentMethodsEditor value={methodsList} onChange={setMethodsList} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setMethodsEditing(null)} className="border-white/10">Cancelar</Button>
            <Button onClick={saveMethods} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="methods-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit/Create plan dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#141414] border-white/10 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="plan-edit-dialog">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">{editing?.__create ? "Crear plan nuevo" : `Editar plan: ${editing?.name}`}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {editing?.__create && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-400">Tipo</Label>
                  <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 h-10 px-3 text-sm" data-testid="form-type">
                    <option value="user">Padelista</option>
                    <option value="club">Club</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-400">ID interno (opcional)</Label>
                  <Input value={form.id || ""} onChange={(e) => setForm({ ...form, id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} className="bg-[#0A0A0A] border-white/20 h-10 font-['JetBrains_Mono']" placeholder="se auto-genera si vacío" data-testid="form-id" />
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-white/20 h-10" data-testid="form-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">Tagline</Label>
              <Input value={form.tagline || ""} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="bg-[#0A0A0A] border-white/20 h-10" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-400">Precio mensual mín. (MXN)</Label>
                <Input type="number" value={form.monthly_price_min ?? ""} onChange={(e) => setForm({ ...form, monthly_price_min: e.target.value })} className="bg-[#0A0A0A] border-white/20 h-10" data-testid="form-price-min" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-400">Precio mensual máx. (opcional)</Label>
                <Input type="number" value={form.monthly_price_max ?? ""} onChange={(e) => setForm({ ...form, monthly_price_max: e.target.value })} className="bg-[#0A0A0A] border-white/20 h-10" placeholder="vacío = mismo que mín." data-testid="form-price-max" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-400">Setup inicial (MXN)</Label>
                <Input type="number" value={form.setup_price ?? ""} onChange={(e) => setForm({ ...form, setup_price: e.target.value })} className="bg-[#0A0A0A] border-white/20 h-10" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-400">Setup máx. (opcional)</Label>
                <Input type="number" value={form.setup_price_max ?? ""} onChange={(e) => setForm({ ...form, setup_price_max: e.target.value })} className="bg-[#0A0A0A] border-white/20 h-10" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">Características (una por línea)</Label>
              <Textarea value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} rows={6} className="bg-[#0A0A0A] border-white/20" placeholder="Reservas y miembros&#10;Reportes&#10;..." data-testid="form-features" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-400">Color</Label>
                <Input type="color" value={form.color || "#CCFF00"} onChange={(e) => setForm({ ...form, color: e.target.value })} className="bg-[#0A0A0A] border-white/20 h-10 w-full p-1" />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer pb-2">
                  <input type="checkbox" checked={!!form.highlight} onChange={(e) => setForm({ ...form, highlight: e.target.checked })} className="w-4 h-4 accent-[#CCFF00]" data-testid="form-highlight" />
                  <span className="text-xs uppercase tracking-widest text-gray-300">Destacar plan</span>
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)} className="border-white/10">Cancelar</Button>
              <Button onClick={submit} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="form-submit">Guardar</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
